import React, { useState, useEffect } from 'react';
import { X, Car, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../../../api.js';

function ToolAssignmentModal({ isOpen, onClose, onSuccess, onMessage, tool, vehicles, sites }) {
  const { t } = useTranslation();
  const [assignmentType, setAssignmentType] = useState('unassigned');
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState('');

  useEffect(() => {
    if (tool) {
      setAssignmentType(tool.assigned_to_type || 'unassigned');
      setSelectedVehicleId(tool.assigned_vehicle_id || '');
      setSelectedLocationId(tool.assigned_location_id || '');
    }
  }, [tool]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (assignmentType === 'vehicle' && !selectedVehicleId) {
      onMessage && onMessage({
        title: t('tools.toast.error'),
        message: t('tools.assignModal.errors.vehicleRequired'),
        type: 'error'
      });
      return;
    }

    if (assignmentType === 'location' && !selectedLocationId) {
      onMessage && onMessage({
        title: t('tools.toast.error'),
        message: t('tools.assignModal.errors.siteRequired'),
        type: 'error'
      });
      return;
    }

    try {
      const updateData = {
        ...tool,
        assigned_to_type: assignmentType,
        assigned_vehicle_id: assignmentType === 'vehicle' ? parseInt(selectedVehicleId) : null,
        assigned_location_id: assignmentType === 'location' ? parseInt(selectedLocationId) : null,
      };

      await api.tool.update(tool.id, updateData);

      onMessage && onMessage({
        title: t('tools.toast.success'),
        message: t('tools.messages.assignmentUpdated'),
        type: 'success'
      });

      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating tool assignment:', error);
      onMessage && onMessage({
        title: t('tools.toast.error'),
        message: t('tools.messages.assignmentError', { error: error.message }),
        type: 'error'
      });
    }
  };

  if (!isOpen || !tool) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
        <div className="bg-amber-500 text-white px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">{t('tools.assignModal.title')}</h2>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">{tool.name}</h3>
            <p className="text-sm text-gray-600">{t('tools.detail.codeLabel', { code: tool.tool_code })}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {t('tools.assignModal.typeLabel')} *
            </label>
            <div className="space-y-3">
              <label className="flex items-center p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="assignmentType"
                  value="vehicle"
                  checked={assignmentType === 'vehicle'}
                  onChange={(e) => setAssignmentType(e.target.value)}
                  className="w-4 h-4 text-amber-600 border-gray-300 focus:ring-amber-500"
                />
                <Car className="w-5 h-5 ml-3 mr-2 text-blue-500" />
                <span className="text-sm font-medium text-gray-900">{t('tools.assignModal.toVehicle')}</span>
              </label>

              <label className="flex items-center p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="assignmentType"
                  value="location"
                  checked={assignmentType === 'location'}
                  onChange={(e) => setAssignmentType(e.target.value)}
                  className="w-4 h-4 text-amber-600 border-gray-300 focus:ring-amber-500"
                />
                <MapPin className="w-5 h-5 ml-3 mr-2 text-green-500" />
                <span className="text-sm font-medium text-gray-900">{t('tools.assignModal.toSite')}</span>
              </label>

              <label className="flex items-center p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="assignmentType"
                  value="unassigned"
                  checked={assignmentType === 'unassigned'}
                  onChange={(e) => setAssignmentType(e.target.value)}
                  className="w-4 h-4 text-amber-600 border-gray-300 focus:ring-amber-500"
                />
                <span className="ml-3 text-sm font-medium text-gray-900">{t('tools.assignModal.unassigned')}</span>
              </label>
            </div>
          </div>

          {assignmentType === 'vehicle' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('tools.assignModal.selectVehicleLabel')} *
              </label>
              <select
                value={selectedVehicleId}
                onChange={(e) => setSelectedVehicleId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                required
              >
                <option value="">{t('tools.assignModal.chooseVehicle')}</option>
                {vehicles.map(vehicle => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.make} {vehicle.model} ({vehicle.license_plate})
                  </option>
                ))}
              </select>
            </div>
          )}

          {assignmentType === 'location' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('tools.assignModal.selectSiteLabel')} *
              </label>
              <select
                value={selectedLocationId}
                onChange={(e) => setSelectedLocationId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                required
              >
                <option value="">{t('tools.assignModal.chooseSite')}</option>
                {sites.map(site => (
                  <option key={site.id} value={site.id}>
                    {site.site_name} - {site.address}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
            >
              {t('tools.actions.assign')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ToolAssignmentModal;
